import { restoreIconString } from '@hr/common/icons.jsx'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row, Tab, Tabs } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getLocaleText } from '../actions/datagrid'
import {
	groupsGetAllQuery,
	personsGetAllQuery,
	procountorAbsencesQuery,
	salaryAbsencesQuery,
	timeTrackingAbsencesSync,
	timeTrackingEntriesQuery,
} from '../api'
import { Button } from '../common/Button.tsx'
import {
	contextMenu,
	defaultColDef,
	gridSettings,
	loadViewState,
	restoreViewState,
	saveViewState,
	sideBar,
	statusBar,
} from '../common/grid'
import { PdfReport } from '../common/pdfreport'
import { fetchSettings } from '../common/settings'
import { dateFormatter, eSalaryDateGetter, hideZeroFormatter } from '../common/utils'
import { queryClient } from '../main'
import { activeIntegrationsSelector, authSelector } from '../reducers'

const timeViewStateKey = 'timeAbsencesViewState'

const initialState = {
	columnVisibility: {
		hiddenColIds: [],
	},
}

export function AbsenceView() {
	const { t, i18n } = useTranslation()
	const resolvedLanguage = i18n.resolvedLanguage

	const gridRef = useRef()
	const timeGridRef = useRef()

	const [_timeReady, setTimeReady] = useState(false)

	const { companyName, salary } = useSelector(authSelector)

	const [settings, setSettings] = useState()

	const [report, setReport] = useState(null)
	const closeReport = () => setReport(null)

	const activeIntegrations = useSelector(activeIntegrationsSelector)
	const isProcountorActive = !!activeIntegrations.tokenProcountor
	const isFinagoTimeActive = !!activeIntegrations.credentialsFinagoTime

	const [activeTab, setActiveTab] = useState('payroll')

	const { data } = useQuery(isProcountorActive ? procountorAbsencesQuery(resolvedLanguage) : salaryAbsencesQuery)

	const { data: personsData } = useQuery(personsGetAllQuery)
	const { data: groupsData } = useQuery(groupsGetAllQuery)

	const employeePayGroupByNr = useMemo(() => {
		if (!personsData?.data) return {}
		return Object.fromEntries(personsData.data.map((p) => [String(p.nr), p.controlDetails?.payGroup ?? '']))
	}, [personsData])

	const groupsMap = useMemo(() => {
		if (!groupsData) return {}
		return Object.fromEntries(groupsData.map((g) => [g.id, g.name]))
	}, [groupsData])

	const employeeGroupByNr = useMemo(() => {
		if (!personsData?.data) return {}
		return Object.fromEntries(
			personsData.data.map((p) => [String(p.nr), groupsMap[p.controlDetails?.employeeGroupId] ?? '']),
		)
	}, [personsData, groupsMap])

	const mappedData = useMemo(() => {
		const raw = isProcountorActive ? data : data?.salaryabsences_response?.salaryabsences
		if (!raw) return null
		return raw.map((row) => ({
			...row,
			payGroup: employeePayGroupByNr[String(row.PERSONNUMBER)] ?? '',
			employeeGroup: employeeGroupByNr[String(row.PERSONNUMBER)] ?? '',
		}))
	}, [isProcountorActive, data, employeePayGroupByNr, employeeGroupByNr])

	const { data: timeData, refetch: refetchTimeData } = useQuery({
		...timeTrackingEntriesQuery('ABSENCES'),
		enabled: isFinagoTimeActive,
	})

	useEffect(() => {
		if (timeData) {
			setTimeReady(true)
		}
	}, [timeData])

	useEffect(() => {
		;(async () => {
			setSettings(await fetchSettings())
		})()
	}, [])

	const timeSyncMutation = useMutation({
		mutationFn: timeTrackingAbsencesSync,
	})

	const refreshTimeAbsences = useCallback(async () => {
		await timeSyncMutation.mutateAsync()
		await refetchTimeData()
	}, [timeSyncMutation.mutateAsync, refetchTimeData])

	const didRun = useRef(false)

	useEffect(() => {
		if (!isFinagoTimeActive) return

		if (didRun.current) return
		didRun.current = true

		refreshTimeAbsences()
	}, [refreshTimeAbsences, isFinagoTimeActive])

	const procountorColumnDefs = !settings
		? undefined
		: [
				{
					headerName: t('PersonNumber', 'Nro'),
					field: 'PERSONNUMBER',
					filter: 'agMultiColumnFilter',
				},
				{
					headerName: t('Person', 'Nimi'),
					field: 'PERSONNAME',
					filter: 'agMultiColumnFilter',
					rowGroup: true,
				},
				{
					headerName: t('PayGroup', 'Maksuryhmä'),
					field: 'payGroup',
					filter: 'agMultiColumnFilter',
					aggFunc: 'first',
				},
				{
					headerName: t('EmployeeGroup', 'Työntekijäryhmä'),
					field: 'employeeGroup',
					filter: 'agMultiColumnFilter',
					aggFunc: 'first',
				},
				{
					headerName: t('AbsenceType', 'Poissaolon tyyppi'),
					field: 'ABSENCETYPE',
					filter: 'agMultiColumnFilter',
					pivot: true,
				},
				{
					headerName: t('PayType', 'Maksutiedon tyyppi'),
					field: 'PAYTYPE',
					filter: 'agMultiColumnFilter',
				},
				{
					headerName: t('PayTypeName', 'Maksutiedon nimi'),
					field: 'PAYTYPENAME',
					filter: 'agMultiColumnFilter',
					pivot: true,
				},
				{
					headerName: t('Hours', 'Tunnit'),
					field: 'HOURS',
					valueFormatter: hideZeroFormatter,
					filter: 'agNumberColumnFilter',
					aggFunc: 'sum',
					sortable: true,
				},
				{
					headerName: t('Days', 'Päivät'),
					field: 'DAYS',
					valueFormatter: hideZeroFormatter,
					filter: 'agNumberColumnFilter',
					aggFunc: 'sum',
					sortable: true,
				},
				{
					headerName: t('Sum', 'Summa'),
					field: 'SUM',
					filter: 'agNumberColumnFilter',
				},
				{
					headerName: t('PeriodName', 'Kauden nimi'),
					field: 'PERIODNAME',
					filter: 'agMultiColumnFilter',
				},
				{
					headerName: t('AbsenceStartDate', 'Alkupäivämäärä'),
					field: 'STARTDATE',
					cellClass: 'dateType',
					filter: 'agDateColumnFilter',
					valueGetter: eSalaryDateGetter,
					valueFormatter: dateFormatter,
				},
				{
					headerName: t('AbsenceEndDate', 'Loppupäivämäärä'),
					field: 'ENDDATE',
					cellClass: 'dateType',
					filter: 'agDateColumnFilter',
					valueGetter: eSalaryDateGetter,
					valueFormatter: dateFormatter,
				},
				{
					headerName: t('PayDate', 'Maksupäivä'),
					field: 'PAYDATE',
					cellClass: 'dateType',
					filter: 'agDateColumnFilter',
					valueGetter: eSalaryDateGetter,
					valueFormatter: dateFormatter,
				},
			]

	const columnDefs = !settings
		? undefined
		: [
				{
					headerName: t('PersonNumber', 'Nro'),
					field: 'PERSONNUMBER',
					filter: 'agMultiColumnFilter',
				},
				{
					headerName: t('Person', 'Nimi'),
					field: 'PERSONNAME',
					filter: 'agMultiColumnFilter',
					rowGroup: true,
				},
				{
					headerName: t('PayGroup', 'Maksuryhmä'),
					field: 'payGroup',
					filter: 'agTextColumnFilter',
					aggFunc: 'first',
				},
				{
					headerName: t('EmployeeGroup', 'Työntekijäryhmä'),
					field: 'employeeGroup',
					filter: 'agTextColumnFilter',
					aggFunc: 'first',
				},
				{
					headerName: t('AbsenceType', 'Poissaolon tyyppi'),
					field: 'ABSENCETYPE',
					filter: 'agMultiColumnFilter',
					pivot: true,
				},
				{
					headerName: t('PayType', 'Maksutiedon tyyppi'),
					field: 'PAYTYPE',
					filter: 'agMultiColumnFilter',
				},
				{
					headerName: t('PayTypeName', 'Maksutiedon nimi'),
					field: 'PAYTYPENAME',
					filter: 'agMultiColumnFilter',
					pivot: true,
				},
				{
					headerName: t('Hours', 'Tunnit'),
					field: 'HOURS',
					valueFormatter: hideZeroFormatter,
					filter: 'agNumberColumnFilter',
					aggFunc: 'sum',
					sortable: true,
				},
				{
					headerName: t('Days', 'Päivät'),
					field: 'DAYS',
					valueFormatter: hideZeroFormatter,
					filter: 'agNumberColumnFilter',
					aggFunc: 'sum',
					sortable: true,
				},
				{
					headerName: t('Days', 'Päivät'),
					field: 'DAYS',
					filter: 'agNumberColumnFilter',
				},
				{
					headerName: t('Sum', 'Summa'),
					field: 'SUM',
					filter: 'agNumberColumnFilter',
				},
				{
					headerName: t('PaymentPeriod', 'Maksukausi'),
					field: 'PERIOD',
					filter: 'agMultiColumnFilter',
				},
				{
					headerName: t('PeriodName', 'Kauden nimi'),
					field: 'PERIODNAME',
					filter: 'agMultiColumnFilter',
				},
				{
					headerName: t('AbsenceStartDate', 'Alkupäivämäärä'),
					field: 'STARTDATE',
					cellClass: 'dateType',
					filter: 'agDateColumnFilter',
					valueGetter: eSalaryDateGetter,
					valueFormatter: dateFormatter,
				},
				{
					headerName: t('AbsenceEndDate', 'Loppupäivämäärä'),
					field: 'ENDDATE',
					cellClass: 'dateType',
					filter: 'agDateColumnFilter',
					valueGetter: eSalaryDateGetter,
					valueFormatter: dateFormatter,
				},
				{
					headerName: settings.dim1name,
					field: 'COSTUNIT',
					filter: 'agMultiColumnFilter',
					valueGetter: (params) => params.data && settings.dim1map?.[params.data.COSTUNIT],
				},
				{
					headerName: settings.dim2name,
					field: 'PROJECT',
					filter: 'agMultiColumnFilter',
					valueGetter: (params) => params.data && settings.dim2map?.[params.data.PROJECT],
				},
				{
					headerName: settings.dim3name,
					field: 'DIM3',
					filter: 'agMultiColumnFilter',
					valueGetter: (params) => params.data && settings.dim3map?.[params.data.DIM3],
				},
				{
					headerName: t('Dimension4', 'Dimensio 4'),
					field: 'DIM4',
					filter: 'agMultiColumnFilter',
				},
				{
					headerName: t('Dimension5', 'Dimensio 5'),
					field: 'DIM5',
					filter: 'agMultiColumnFilter',
				},
				{
					headerName: t('PayDate', 'Maksupäivä'),
					field: 'PAYDATE',
					cellClass: 'dateType',
					filter: 'agDateColumnFilter',
					valueGetter: eSalaryDateGetter,
					valueFormatter: dateFormatter,
				},
			]

	const timeColumnDefs = [
		{
			headerName: t('ID', 'Tunniste'),
			field: 'id',
			width: 90,
			sortable: true,
			filter: 'agNumberColumnFilter',
			hide: true,
		},
		{
			headerName: t('Name', 'Nimi'),
			field: 'employeeName',
			filter: 'agMultiColumnFilter',
		},
		{
			headerName: t('EmployeeSourceID', 'Työntekijän lähdetunniste'),
			field: 'employeeSourceId',
			filter: 'agMultiColumnFilter',
			hide: true,
		},
		{
			headerName: t('SourceID', 'Lähdetunniste'),
			field: 'sourceId',
			filter: 'agMultiColumnFilter',
			hide: true,
		},
		{
			headerName: t('AbsenceType', 'Poissaolon tyyppi'),
			field: 'eventTypeName',
			filter: 'agMultiColumnFilter',
		},
		{
			headerName: t('EventTypeID', 'Tapahtuman tyyppitunniste'),
			field: 'eventTypeId',
			filter: 'agNumberColumnFilter',
			hide: true,
		},
		{
			headerName: t('Hours', 'Tunnit'),
			field: 'durationInHours',
			filter: 'agNumberColumnFilter',
			sortable: true,
			valueFormatter: (p) => (p.value != null ? hideZeroFormatter(p) : null),
		},
		{
			headerName: t('AbsenceStartDate', 'Alkupäivämäärä'),
			field: 'startTime',
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueGetter: (params) => (params.data?.startTime ? new Date(params.data.startTime) : null),
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('AbsenceEndDate', 'Loppupäivämäärä'),
			field: 'endTime',
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueGetter: (params) => (params.data?.endTime ? new Date(params.data.endTime) : null),
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('ApprovedByEmployee', 'Työntekijän hyväksymä'),
			field: 'approvedByEmployee',
			filter: 'agSetColumnFilter',
			valueFormatter: (params) => (params.value ? t('Yes', 'Kyllä') : t('No', 'Ei')),
		},
		{
			headerName: t('ApprovedByCustomer', 'Asiakkaan hyväksymä'),
			field: 'approvedByCustomer',
			filter: 'agSetColumnFilter',
			valueFormatter: (params) => (params.value ? t('Yes', 'Kyllä') : t('No', 'Ei')),
		},
		{
			headerName: t('Deleted', 'Poistettu'),
			field: 'deleted',
			filter: 'agSetColumnFilter',
			valueFormatter: (params) => (params.value ? t('Yes', 'Kyllä') : t('No', 'Ei')),
			hide: true,
		},
		{
			headerName: t('SourceDeleted', 'Poistettu lähteestä'),
			field: 'sourceDeleted',
			filter: 'agSetColumnFilter',
			valueFormatter: (params) => (params.value ? t('Yes', 'Kyllä') : t('No', 'Ei')),
			hide: true,
		},
		{
			headerName: t('WorkUnit', 'Työkohde'),
			field: 'workUnitName',
			filter: 'agMultiColumnFilter',
			hide: true,
		},
	]

	if (!salary && columnDefs) {
		columnDefs.splice(5, 1) // remove sum column
	}

	const getContextMenuItems = useCallback(
		(params) => {
			return contextMenu(t, params, setReport, {
				title: t('AbsenceStatistics', 'Poissaolotilastot'),
				companyname: companyName,
			})
		},
		[t, companyName],
	)

	const getTimeContextMenuItems = useCallback(
		(params) => {
			let items = []

			items.push({
				name: t('DefaultGridValues', 'Palauta taulukon oletusasetukset'),
				action: () => {
					restoreViewState(timeViewStateKey, initialState)
				},
				icon: restoreIconString,
			})

			items.push('separator')

			items = items.concat(
				contextMenu(t, params, setReport, {
					title: t('Absences', 'Poissaolot'),
				}),
			)

			return items
		},
		[t],
	)

	const handleTabSelect = (activeKey) => {
		setActiveTab(activeKey)

		if (activeKey === 'payroll' && gridRef.current?.api) {
			gridRef.current.api.sizeColumnsToFit()
		} else if (activeKey === 'time' && isFinagoTimeActive && timeGridRef.current?.api) {
			timeGridRef.current.api.sizeColumnsToFit()
			setTimeReady(false)
		}
	}

	return (
		<>
			{report !== null && <PdfReport json={report} onCloseReport={closeReport} />}
			<Row className="my-1">
				<Col sm="4">
					<h2 className="viewheading">{t('AbsenceStatistics', 'Poissaolotilastot')}</h2>
				</Col>
				<Col sm="8" align="right">
					{activeTab === 'payroll' && (
						<Button
							testId="update"
							variant="primary"
							disabled={!data}
							onClick={() =>
								queryClient.invalidateQueries({
									queryKey: [isProcountorActive ? 'procountorAbsences' : 'salaryAbsences'],
								})
							}
						>
							{t('Update', 'Päivitä')}
						</Button>
					)}

					{isFinagoTimeActive && activeTab === 'time' && (
						<Button testId="update-time" variant="primary" onClick={refreshTimeAbsences}>
							{t('UpdateTimeAbsences', 'Päivitä poissaolot')}
						</Button>
					)}
				</Col>
			</Row>

			<Tabs defaultActiveKey="payroll" onSelect={handleTabSelect} className="mt-3">
				<Tab eventKey="payroll" title={t('FromPayroll', 'Palkanlaskennasta')}>
					<Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
						<AgGridReact
							{...gridSettings}
							loading={!data}
							ref={gridRef}
							columnDefs={isProcountorActive ? procountorColumnDefs : columnDefs}
							defaultColDef={{ ...defaultColDef, filter: 'agNumberColumnFilter' }}
							sideBar={sideBar}
							rowData={mappedData}
							getContextMenuItems={getContextMenuItems}
							statusBar={statusBar}
							pivotMode
							pivotDefaultExpanded={1}
							suppressAggFuncInHeader
							getLocaleText={(params) => getLocaleText(t, params)}
							animateRows
							suppressCellFocus
							enableCellTextSelection
							debug={import.meta.env.DEV}
						/>
					</Row>
				</Tab>

				{isFinagoTimeActive && (
					<Tab eventKey="time" title={t('FromTimeTracking', 'Time työajanseurannasta')}>
						<Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
							<AgGridReact
								{...gridSettings}
								loading={!timeData}
								ref={timeGridRef}
								columnDefs={timeColumnDefs}
								defaultColDef={{ ...defaultColDef, filter: 'agNumberColumnFilter' }}
								sideBar={sideBar}
								rowData={timeData}
								getContextMenuItems={getTimeContextMenuItems}
								statusBar={statusBar}
								getLocaleText={(params) => getLocaleText(t, params)}
								animateRows
								suppressCellFocus
								enableCellTextSelection
								initialState={loadViewState(timeViewStateKey) || initialState}
								onStateUpdated={({ state }) => saveViewState(state, timeViewStateKey)}
								debug={import.meta.env.DEV}
							/>
						</Row>
					</Tab>
				)}
			</Tabs>
		</>
	)
}
